import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate } from "@angular/router";
import { Store } from "@ngrx/store";
import { IauiVerificationAction, IauiVerificationSelector } from "@obg/ngx.common";
import {
	AuthSelector,
	BankDetailsVerificationStatus,
	CORE_ROUTE,
	CoreState,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { RouteAction, RouteConfig, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";

@Injectable({
	providedIn: "root",
})
export class IauiVerificationGuard implements CanActivate {
	constructor(
		private store: Store<CoreState>,
		private iauiVerificationAction: IauiVerificationAction,
		private iauiVerificationSelector: IauiVerificationSelector
	) {}

	canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
		return this.store.select(AuthSelector.isAuthenticated).pipe(
			switchMap((isAuthenticated) =>
				!isAuthenticated
					? of(true)
					: combineLatest([
							this.store.select(JurisdictionSelector.get),
							this.store.select(this.iauiVerificationSelector.getSelectedPayment()),
							this.store.select(CustomerManagementSelector.getCustomer).pipe(
								ignoreNil(),
								map((customer) => customer.profile.bankDetailsVerificationStatus)
							),
							this.store.select(RouteSelector.getCurrent),
						]).pipe(
							take(1),
							map(([jurisdiction, paymentType, status, currentRoute]) => {
								const isVerified = status === BankDetailsVerificationStatus.Verified;
								const isPending = status === BankDetailsVerificationStatus.Pending;
								const isDenied = status === BankDetailsVerificationStatus.Denied;
								const isDepositRoute =
									(route.routeConfig as RouteConfig)?.name === CORE_ROUTE.payment.deposit;
								if (
									jurisdiction !== Jurisdiction.Iaui ||
									(isDepositRoute && (paymentType || isPending || isDenied)) ||
									isVerified
								) {
									return true;
								}

								const returnUrl = currentRoute?.queryParams?.returnUrl;
								const isLoginPage = currentRoute?.name === CORE_ROUTE.auth.login;

								if (!currentRoute || (isLoginPage && returnUrl)) {
									this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
								}
								this.store.dispatch(this.iauiVerificationAction.openModal());

								return false;
							})
						)
			)
		);
	}
}
